/**
 * Search Page Module
 * Handles full search page functionality with results display
 */

import { escapeHtml, formatPrice } from '../core/utils.js';

export class SearchPage {
    constructor(system) {
        this.system = system;
        this.initialized = false;
    }

    init() {
        // Only initialize if we're on a search page
        if (typeof window.templateType === 'undefined' || window.templateType !== 'Search') {
            console.log('SearchPage: Not a search page, skipping initialization');
            return;
        }

        console.log('SearchPage: Initializing search page functionality');
        this.cacheElements();
        this.performSearch();
        this.initialized = true;
    }

    cacheElements() {
        this.loadingEl = document.getElementById('loading');
        this.errorEl = document.getElementById('error');
        this.resultsEl = document.getElementById('results');
        this.headerEl = document.getElementById('results-header');
    }

    /**
     * Get merchant ID from various sources
     * @returns {string|null} Merchant ID
     */
    getMerchantId() {
        // Check multiple sources in priority order
        const sources = [
            () => this.system?.config?.checkout?.merchantId,
            () => window.ecommerce?.config?.checkout?.merchantId,
            () => document.querySelector('[data-merchant-id]')?.dataset.merchantId,
            () => window.merchantId
        ];

        for (const source of sources) {
            try {
                const id = source();
                if (id) {
                    return id;
                }
            } catch (error) {
                // Continue to next source if this one fails
                continue;
            }
        }

        return null;
    }

    /**
     * Get query parameter from URL
     * @param {string} param - Parameter name
     * @returns {string|null} Parameter value
     */
    getQueryParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    /**
     * Format price with "kr" suffix (specific for search page)
     * @param {number} price - Price to format
     * @returns {string} Formatted price
     */
    formatPriceWithKr(price) {
        return price + ' kr';
    }

    /**
     * Create HTML for a brand
     * @param {Object} brand - Brand data
     * @returns {string} Brand HTML
     */
    createBrandHTML(brand) {
        return `
            <a href="${escapeHtml(brand.brandUrl || '#')}" class="card-item search-brand-item">
                <div class="image-wrapper of-wrapper">
                    <img src="${escapeHtml(brand.imageUrl || '')}" alt="${escapeHtml(brand.name)}" class="cart-image" />
                </div>
                <div class="card-body">
                    <p class="product-name text-bold">
                        <span>${escapeHtml(brand.name)}</span>
                    </p>
                    <p class="cart-description">
                        <span>${brand.activeProductCount || 0} produkter</span>
                    </p>
                </div>
                <div class="card-footer">
                    <span class="btn btn-primary-filled">Visa produkter</span>
                </div>
            </a>
        `;
    }

    /**
     * Create HTML for a category
     * @param {Object} category - Category data
     * @returns {string} Category HTML
     */
    createCategoryHTML(category) {
        return `
            <a href="${escapeHtml(category.categoryUrl || '#')}" class="card-item search-category-item">
                <div class="image-wrapper of-wrapper">
                    <img src="${escapeHtml(category.imageUrl || '')}" alt="${escapeHtml(category.name)}" class="cart-image" />
                </div>
                <div class="card-body">
                    <p class="product-name text-bold">
                        <span>${escapeHtml(category.name)}</span>
                    </p>
                    <p class="cart-description">
                        <span>${escapeHtml(category.description || '')}</span>
                    </p>
                    <p class="cart-count">
                        <span>${category.activeProductCount || 0} produkter</span>
                    </p>
                </div>
                <div class="card-footer">
                    <span class="btn btn-primary-filled">Visa produkter</span>
                </div>
            </a>
        `;
    }

    /**
     * Create HTML for a product
     * @param {Object} product - Product data
     * @returns {string} Product HTML
     */
    createProductHTML(product) {
        const price = product.discountPrice ? product.discountPrice : product.price;
        
        return `
            <a href="${escapeHtml(product.productUrl || '#')}" class="card-item js-cart-product">
                <div class="image-wrapper of-wrapper">
                    <img src="${escapeHtml(product.firstImage || '')}" 
                         alt="${escapeHtml(product.name)}" 
                         class="cart-image js-cart-image"
                         loading="lazy" />
                </div>
                <div class="card-body">
                    <p class="product-name text-bold">
                        <span class="js-cart-name">${escapeHtml(product.name)}</span>
                    </p>
                    <p class="cart-price">
                        <span class="js-cart-price">${this.formatPriceWithKr(price)}</span>
                    </p>
                </div>
                <div class="card-footer">
                    <span class="btn btn-primary-filled js-add-to-cart">Läs mer & köp</span>
                </div>
            </a>
        `;
    }

    /**
     * Make API call for search
     * @param {string} query - Search query
     * @param {string} brand - Brand URL slug filter
     * @returns {Promise<Object>} Search results
     */
    async searchProducts(query, brand) {
        // Get merchant ID dynamically
        const merchantId = this.getMerchantId();
        
        if (!merchantId) {
            throw new Error('Merchant ID could not be found. Please ensure the system is properly configured.');
        }
        
        // Build API URL with dynamic merchant ID
        let apiUrl = `/cart/api/v1/Search/${merchantId}/products-and-categories?pageSize=20`;
        
        // Add query parameter if provided
        if (query) {
            apiUrl += `&query=${encodeURIComponent(query)}`;
        }
        
        // Add brand parameter if provided
        if (brand) {
            apiUrl += `&brand=${encodeURIComponent(brand)}`;
        }
        
        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'accept': '*/*'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('SearchPage: Fetch error:', error);
            throw error;
        }
    }

    /**
     * Display search results
     * @param {Object} data - Search results data
     * @param {string} query - Search query
     */
    displayResults(data, query) {
        if (!this.loadingEl || !this.errorEl || !this.resultsEl || !this.headerEl) {
            console.error('SearchPage: Required elements not found');
            return;
        }

        // Hide loading and error
        this.loadingEl.style.display = 'none';
        this.errorEl.style.display = 'none';

        const totalItems = data.products.totalItems + (data.brands?.totalItems || 0) + (data.categories?.totalItems || 0);
        
        // Update header
        this.headerEl.textContent = `${totalItems} sökresultat för "${query}"`;

        if (totalItems === 0) {
            this.resultsEl.innerHTML = '<div class="no-results"><p>Inga resultat hittades för din sökning.</p></div>';
        } else {
            let html = '';
            
            // Create HTML for brands first (if any)
            if (data.brands?.items?.length > 0) {
                html += '<div class="search-section-header"><p class="small-title">Varumärken</p></div>';
                html += data.brands.items.map(brand => this.createBrandHTML(brand)).join('');
            }
            
            // Create HTML for categories (if any)
            if (data.categories?.items?.length > 0) {
                html += '<div class="search-section-header"><p class="small-title">Kategorier</p></div>';
                html += data.categories.items.map(category => this.createCategoryHTML(category)).join('');
            }
            
            // Create HTML for products
            if (data.products?.items?.length > 0) {
                html += '<div class="search-section-header"><p class="small-title">Produkter</p></div>';
                html += data.products.items.map(product => this.createProductHTML(product)).join('');
            }
            
            this.resultsEl.innerHTML = html;
        }

        this.resultsEl.style.display = 'flex';
    }

    /**
     * Display error message
     * @param {string} message - Error message
     */
    displayError(message) {
        if (!this.loadingEl || !this.errorEl || !this.headerEl) {
            console.error('SearchPage: Required elements not found');
            return;
        }

        this.loadingEl.style.display = 'none';
        this.headerEl.textContent = 'Fel vid sökning';
        this.errorEl.textContent = message;
        this.errorEl.style.display = 'block';
    }

    /**
     * Main search function
     */
    async performSearch() {
        const query = this.getQueryParam('q');
        const brand = this.getQueryParam('b');
        
        // At least one parameter must be provided
        if (!query && !brand) {
            this.displayError('Ingen sökterm eller brand angiven i URL:en (parameter "q" eller "b" saknas).');
            return;
        }

        try {
            const data = await this.searchProducts(query, brand);
            
            // Build display text
            let displayText = '';
            if (query && brand) {
                displayText = `"${query}" i ${brand}`;
            } else if (query) {
                displayText = `"${query}"`;
            } else {
                displayText = brand;
            }
            
            this.displayResults(data, displayText);
        } catch (error) {
            this.displayError(`Kunde inte utföra sökning: ${error.message}`);
        }
    }

    /**
     * Check if module is initialized
     * @returns {boolean} Initialization status
     */
    isInitialized() {
        return this.initialized;
    }

    /**
     * Destroy the module
     */
    destroy() {
        this.initialized = false;
        // Clear cached elements
        this.loadingEl = null;
        this.errorEl = null;
        this.resultsEl = null;
        this.headerEl = null;
    }
}
